import axiosInstance from "./apiinterseptor";

export type HotlinePerson = {
  id: string;
  username: string;
  email: string;
  phone: string | null;
  role: string;
  level: number;
  managerId: string | null;
};

export type HotlineItem = {
  id: string;
  assignedToUserId: string;
  candidateName: string;
  phone: string;
  city: string | null;
  candidateTitle: string | null;
  jobTitle: string | null;
  companyName: string | null;
  sourceType?: string | null;
  status: string;
  lastDisposition?: string | null;
  category: string;
  priority: number;
  attemptCount: number;
  appliedAt: string;
};

export type HotlineJobOption = {
  id: string;
  title: string;
  companyName: string;
  location: string;
};

export async function getHotlineJobs(params?: { search?: string }): Promise<{
  jobs: HotlineJobOption[];
  total: number;
}> {
  const response = await axiosInstance.get("/hotline-queue/jobs", {
    params: { limit: 20, search: params?.search || undefined },
  });
  const jobs = (response.data?.data?.jobs ?? []) as HotlineJobOption[];
  return {
    jobs,
    total: Number(response.data?.data?.total ?? jobs.length),
  };
}

export type HotlineHotLeadRecruiterSetting = {
  userId: string;
  enabled: boolean;
  maxLeads: number | null;
  passedCount: number;
};

export async function getHotlineHotLeadSettings(scopeUserId: string) {
  const response = await axiosInstance.get("/hotline-queue/hot-leads/settings", {
    params: { scopeUserId },
  });
  return response.data?.data as {
    scopeUserId: string;
    teamEnabled: boolean;
    recruiters: HotlineHotLeadRecruiterSetting[];
  };
}

export async function saveHotlineHotLeadTeam(payload: { scopeUserId: string; enabled: boolean }) {
  const response = await axiosInstance.put("/hotline-queue/hot-leads/team", payload);
  return response.data?.data as { scopeUserId: string; teamEnabled: boolean };
}

export async function saveHotlineHotLeadRecruiter(payload: {
  userId: string;
  enabled?: boolean;
  maxLeads?: number | null;
}) {
  const response = await axiosInstance.put("/hotline-queue/hot-leads/recruiter", payload);
  return response.data?.data as HotlineHotLeadRecruiterSetting;
}

export type HotlineLiveSheetConfig = {
  source: string;
  spreadsheetUrl: string;
  tabName: string;
  candidateNameColumn: string;
  numberColumn: string;
  jobIdColumn: string;
  live: boolean;
  lastReadAt: string | null;
  lastError: string | null;
  lastStats: {
    read: number;
    added: number;
    passed: number;
    waiting: number;
    skipped: number;
  } | null;
};

export async function getHotlineLiveSheets() {
  const response = await axiosInstance.get("/hotline-queue/live-sheets");
  return (response.data?.data?.sheets ?? []) as HotlineLiveSheetConfig[];
}

export async function saveHotlineLiveSheet(
  source: string,
  payload: {
    spreadsheetUrl: string;
    tabName: string;
    candidateNameColumn: string;
    numberColumn: string;
    jobIdColumn: string;
    live: boolean;
  },
) {
  const response = await axiosInstance.put(`/hotline-queue/live-sheets/${source}`, payload);
  return response.data?.data?.sheet as HotlineLiveSheetConfig;
}

export async function getHotlineLiveSheetBranches(scopeUserId: string) {
  const response = await axiosInstance.get("/hotline-queue/live-sheets/branches", {
    params: { scopeUserId },
  });
  return response.data?.data as {
    scopeUserId: string;
    sources: Array<{ source: string; enabled: boolean }>;
  };
}

export async function saveHotlineLiveSheetBranch(payload: {
  scopeUserId: string;
  source: string;
  enabled: boolean;
}) {
  const response = await axiosInstance.put("/hotline-queue/live-sheets/branch", payload);
  return response.data?.data as { scopeUserId: string; source: string; enabled: boolean };
}

export async function getHotlineTeam(params?: { limit?: number; search?: string }): Promise<{
  people: HotlinePerson[];
  total: number;
}> {
  const response = await axiosInstance.get("/hotline-queue/team", { params });
  const people = (response.data?.data?.people ?? []) as HotlinePerson[];
  return {
    people,
    total: Number(response.data?.data?.total ?? people.length),
  };
}

export async function getHotlineAssignments(): Promise<HotlineItem[]> {
  const response = await axiosInstance.get("/hotline-queue", {
    params: { scope: "all", sort: "priority" },
  });
  return response.data?.data?.items ?? [];
}

export async function createHotlineAssignment(payload: {
  candidateName: string;
  phone: string;
  city?: string;
  jobTitle?: string;
  companyName?: string;
  category?: string;
  priority?: number;
  assignedToUserId: string;
}) {
  const response = await axiosInstance.post("/hotline-queue", payload);
  return response.data?.data?.item as HotlineItem;
}

export async function importHotlineAssignments(payload: {
  assignedToUserId: string;
  jobTitle?: string;
  companyName?: string;
  rows: Array<{ candidateName: string; phone: string }>;
}) {
  const response = await axiosInstance.post("/hotline-queue/import", payload);
  return {
    items: (response.data?.data?.items ?? []) as HotlineItem[],
    skipped: (response.data?.data?.skipped ?? []) as Array<{ row: number; reason: string }>,
  };
}

export type HotlinePresence = {
  recruiterId: string;
  name: string;
  status: "on_call" | "break" | "working" | "off" | string;
  breakReason: string | null;
  startedAt: string | null;
  endedAt: string | null;
  todaySeconds: number;
  callingSeconds: number;
  callCount: number;
};

export type HotlineWorkSegment = {
  id: string;
  activity: string;
  startedAt: string;
  endedAt: string | null;
  breakReason: string | null;
};

export type HotlineWorkShift = {
  id: string;
  recruiterId: string;
  startedAt: string;
  endedAt: string | null;
  segments: HotlineWorkSegment[];
};

export type HotlineStatusCounts = {
  recruiterId: string;
  name: string;
  role: string;
  managerId?: string | null;
  attempts: number;
  statuses: Record<string, number>;
};

export type HotlineTrackedCall = {
  id: string;
  recruiterId: string;
  candidateName: string;
  phone: string;
  jobTitle: string | null;
  companyName: string | null;
  source: string | null;
  startedAt: string;
  disposition: string | null;
  note: string | null;
  recruiterName?: string;
};

export type HotlinePresenceCounts = {
  on_call: number;
  break: number;
  working: number;
  off: number;
  totalOnline: number;
};

export async function getHotlinePresence(
  page = 1,
  search = "",
  sort: "status" | "work" | "calls" = "status",
  limit = 20,
): Promise<{
  items: HotlinePresence[];
  total: number;
  page: number;
  counts: HotlinePresenceCounts;
}> {
  const response = await axiosInstance.get("/hotline-queue/work/team", {
    params: { page, limit, sort, ...(search.trim() ? { search: search.trim() } : {}) },
  });
  const items = (response.data?.data?.items ?? []) as HotlinePresence[];
  const raw = response.data?.data?.counts ?? {};
  return {
    items,
    total: Number(response.data?.data?.total ?? items.length),
    page: Number(response.data?.data?.page ?? page),
    counts: {
      on_call: Number(raw.on_call ?? 0),
      break: Number(raw.break ?? 0),
      working: Number(raw.working ?? 0),
      off: Number(raw.off ?? 0),
      totalOnline: Number(raw.totalOnline ?? 0),
    },
  };
}

export async function getHotlineWorkTimeline(recruiterId: string): Promise<HotlineWorkShift[]> {
  const response = await axiosInstance.get("/hotline-queue/work", {
    params: { recruiterId },
  });
  return response.data?.data?.shifts ?? [];
}

export async function getHotlineStatusSummary(
  from: string,
  to: string,
  page = 1,
  search = "",
  all = false,
): Promise<{ items: HotlineStatusCounts[]; total: number; page: number }> {
  const response = await axiosInstance.get("/hotline-queue/calls/summary", {
    params: all
      ? { from, to, tree: 1, ...(search.trim() ? { search: search.trim() } : {}) }
      : { from, to, page, limit: 20, ...(search.trim() ? { search: search.trim() } : {}) },
  });
  const items = (response.data?.data?.items ?? []) as HotlineStatusCounts[];
  return {
    items,
    total: Number(response.data?.data?.total ?? items.length),
    page: Number(response.data?.data?.page ?? page),
  };
}

export async function getHotlineTeamCalls(params: {
  from: string;
  to: string;
  page: number;
  disposition?: string;
}): Promise<{ items: HotlineTrackedCall[]; total: number; page: number }> {
  const response = await axiosInstance.get("/hotline-queue/calls/team", {
    params: {
      from: params.from,
      to: params.to,
      page: params.page,
      limit: 20,
      ...(params.disposition ? { disposition: params.disposition } : {}),
    },
  });
  const items = (response.data?.data?.items ?? []) as HotlineTrackedCall[];
  return {
    items,
    total: Number(response.data?.data?.total ?? items.length),
    page: Number(response.data?.data?.page ?? params.page),
  };
}

export async function getHotlineTrackedCalls(params: {
  recruiterId: string;
  from: string;
  to: string;
  page: number;
  disposition?: string;
}): Promise<{ items: HotlineTrackedCall[]; hasMore: boolean }> {
  const response = await axiosInstance.get("/hotline-queue/calls", {
    params: {
      ...params,
      limit: 20,
      ...(params.disposition ? { disposition: params.disposition } : {}),
    },
  });
  return {
    items: response.data?.data?.items ?? [],
    hasMore: response.data?.data?.hasMore === true,
  };
}

export async function reassignHotlineItem(id: string, assignedToUserId: string) {
  const response = await axiosInstance.patch(`/hotline-queue/${id}/assign`, {
    assignedToUserId,
  });
  return response.data?.data?.item as HotlineItem;
}

export async function removeHotlineItem(id: string) {
  await axiosInstance.delete(`/hotline-queue/${id}`);
}
